import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Tag from "senf-atomic-design-system/src/components/atoms/tag/Tag";
import { useAuthContext } from "senf-shared";
import Button from "../../core/button/Button";
import Toast from "../../core/toast/Toast";
import { useModals } from "../../molecules/modalStack/ModalProvider";

declare const __APP_VERSION__: string;

const UPDATE_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes

function useCheckForUpdates(reload = false) {
  const [updateAvailable, setUpdateAvailable] = useState<
    "major" | "minor" | "patch" | "none"
  >("none");
  const [appVersion, setAppVersion] = useState({
    current: "",
    latest: "",
  });
  const latestVersionRef = useRef<string | null>(null);

  const checkForUpdate = useCallback(() => {
    const currentVersion = __APP_VERSION__;

    if (!currentVersion) return;

    // Add timestamp to prevent browser caching
    fetch(`/meta.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    })
      .then((res) => res.json())
      .then((meta) => {
        const latestVersion = meta?.version;
        // console.log("meta", latestVersion, currentVersion);

        // Only proceed if a version was fetched and it's different from the last one we knew about
        if (!latestVersion || latestVersion === latestVersionRef.current) {
          return;
        }
        latestVersionRef.current = latestVersion;

        setAppVersion({
          current: currentVersion,
          latest: latestVersion,
        });

        if (latestVersion === currentVersion) {
          setUpdateAvailable("none");
          return;
        }

        const [currentMajor, currentMinor, currentPatch] = currentVersion
          .split(".")
          .map(Number);
        const [metaMajor, metaMinor, metaPatch] = latestVersion
          .split(".")
          .map(Number);

        const isMajor = metaMajor > currentMajor;
        const isMinor = !isMajor && metaMinor > currentMinor;
        const isPatch = !isMajor && !isMinor && metaPatch > currentPatch;

        const updateType = isMajor
          ? "major"
          : isMinor
            ? "minor"
            : isPatch
              ? "patch"
              : "none"; // Default to none if somehow versions are equal or older

        setUpdateAvailable(updateType);

        if (updateType !== "none") {
          // console.log(
          //   `[App] New ${updateType} version detected: ${latestVersion}`,
          // );
          if (reload) {
            // console.log("[App] Reload triggered by update check...");
            window.location.reload();
          }
        }
      })
      .catch((err) => {
        console.warn("[App] Version check failed", err);
        if (window.Sentry) {
          window.Sentry.captureException(err);
        }
      });
  }, [reload]);

  useEffect(() => {
    checkForUpdate();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // console.log("[App] Checking for update due to visibility change");
        checkForUpdate();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const intervalId = setInterval(() => {
      // console.log("[App] Checking for update due to interval");
      checkForUpdate();
    }, UPDATE_CHECK_INTERVAL);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(intervalId);
      // console.log("[App] Cleaned up update check interval and listener");
    };
  }, [checkForUpdate]);

  return {
    checkForUpdate,
    updateAvailable,
    appVersion,
  };
}

const UpdateTitle = ({
  appVersion,
}: {
  appVersion: { current: string; latest: string };
}) => {
  const { t } = useTranslation("system");
  return (
    <div className="flex flex-col items-start gap-2">
      <Tag color="green" text={appVersion.latest} interactive={false} />
      <span>{t("update.newVersionAvailable")}</span>
    </div>
  );
};

export default function UpdateModal() {
  const { updateAvailable, appVersion } = useCheckForUpdates();
  const { openModal, closeModal } = useModals();
  const { user } = useAuthContext();
  const { t } = useTranslation("system");

  // useEffect(() => {
  //   const isLocalhost =
  //     window.location.hostname === "localhost" ||
  //     window.location.hostname === "127.0.0.1";

  //   const isMajorOrMinorUpdate =
  //     updateAvailable === "major" || updateAvailable === "minor";

  //   if (isMajorOrMinorUpdate) {
  //     const description = appVersion.latest
  //       ? t("update.updateToVersion", { version: appVersion.latest })
  //       : t("update.updateToLatest");

  //     openModal(null, {
  //       id: "updateAvailable",
  //       title: (<UpdateTitle appVersion={appVersion} />) as unknown as string,
  //       description,
  //       submitText: t("update.updateNow"),
  //       onClose: false,
  //       preventClose: false,
  //       onSubmit: () => {
  //         window.location.reload();
  //       },
  //     });
  //   } else if (updateAvailable === "patch") {
  //     closeModal({ id: "updateAvailable" });
  //     // console.log("[App] Patch update detected. Attempting to clear caches...");
  //     if ("caches" in window) {
  //       caches
  //         .keys()
  //         .then((cacheNames) => {
  //           // console.log("[App] Cache names:", cacheNames);
  //           // return Promise.all(
  //           //   cacheNames.map((cacheName) => caches.delete(cacheName)),
  //           // );
  //         })
  //         .then(() => {
  //           console.log("[App] Cache API storage cleared (if any existed).");
  //         })
  //         .catch((err) => {
  //           // console.warn("[App] Failed to clear Cache API storage:", err);
  //         });
  //     }
  //   }
  // }, [
  //   updateAvailable,
  //   appVersion,
  //   user?.isSuperAdmin,
  //   openModal,
  //   closeModal,
  //   t,
  // ]);
}
